import type Peer from "peerjs";
import type { DataConnection, PeerError } from "peerjs";

/** Tempo para a conexão abrir antes de marcar a presença como desconhecida. */
const PROBE_TIMEOUT_MS = 4000;

export type TeleconsultoriaPresenceStatus =
  | "checking"
  | "online"
  | "offline"
  | "unknown";

/**
 * Tenta abrir uma conexão com `tc-{cpf}` e fecha em seguida.
 * `peer-unavailable` = offline. Timeout ou outro erro = desconhecido.
 * Não usa listAllPeers: no broker público isso lista peers de outros sistemas.
 */
export function probeTeleconsultoriaPeer(
  peer: Peer,
  targetId: string,
  isCancelled: () => boolean,
): Promise<TeleconsultoriaPresenceStatus> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (status: TeleconsultoriaPresenceStatus) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      peer.off("error", onPeerError);
      resolve(status);
    };

    const timer = window.setTimeout(() => finish("unknown"), PROBE_TIMEOUT_MS);

    const onPeerError = (err: PeerError<string>) => {
      if (err.type !== "peer-unavailable") return;
      // A mensagem do PeerJS é "Could not connect to peer {id}".
      if (err.message !== `Could not connect to peer ${targetId}`) return;
      finish("offline");
    };

    peer.on("error", onPeerError);

    if (isCancelled() || peer.destroyed || !peer.open) {
      finish("unknown");
      return;
    }

    let conn: DataConnection;
    try {
      conn = peer.connect(targetId, {
        metadata: { kind: "teleconsultoria-presence" },
      });
    } catch {
      finish("unknown");
      return;
    }

    conn.on("open", () => {
      try {
        conn.close();
      } catch {
        // O peer de presença também fecha esta conexão.
      }
      finish("online");
    });
  });
}
